import React, { useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { getProduct } from "../mock";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import Reviews from "../components/Reviews";
import { useCart } from "../context/CartContext";
import { useWishlist } from "../context/WishlistContext";
import { ChevronLeft, ChevronRight, ChevronDown, Share2, Heart, Star, Truck, MapPin, PackageCheck, Shirt, Info } from "lucide-react";

export default function ProductDetail() {
  const { slug } = useParams();
  const product = getProduct(slug);
  const navigate = useNavigate();
  const { addItem } = useCart();
  const { has: wishHas, toggle: wishToggle } = useWishlist();
  const [color, setColor] = useState(product?.swatches?.[0]);
  const [activeImg, setActiveImg] = useState(0);
  const [showAllColors, setShowAllColors] = useState(false);

  if (!product) {
    return (
      <div className="min-h-screen bg-[color:var(--dms-cream)]">
        <Navbar />
        <div className="max-w-3xl mx-auto py-32 text-center">
          <h2 className="font-serif text-3xl text-[color:var(--dms-navy)]">Product not found</h2>
          <Link to="/" className="link-peach mt-6 inline-flex">Back to home</Link>
        </div>
        <Footer />
      </div>
    );
  }

  const gallery = product.gallery || [product.img];
  const addSample = () => {
    addItem({ slug: product.slug, title: `${product.title} — Sample`, price: product.price,
      img: product.img, color: color.hex, colorName: color.name, size: "M", qty: 1 });
  };
  const startDesigning = () => navigate(`/design/${product.slug}?color=${encodeURIComponent(color.hex)}`);

  const visibleColors = showAllColors ? product.swatches : product.swatches.slice(0, 12);

  return (
    <div className="min-h-screen bg-[color:var(--dms-cream)]">
      <Navbar />
      <div className="max-w-7xl mx-auto px-5 lg:px-8 pt-6">
        {/* Breadcrumb */}
        <nav className="text-xs tracking-widest uppercase text-[color:var(--dms-navy)]/55 flex items-center gap-2 flex-wrap">
          <Link to="/" className="hover:text-[color:var(--dms-navy)]">All Products</Link>
          <ChevronRight className="w-3 h-3" />
          <Link to="/polos" className="hover:text-[color:var(--dms-navy)]">Polos</Link>
          <ChevronRight className="w-3 h-3" />
          <span className="text-[color:var(--dms-navy)]">{product.title}</span>
        </nav>
      </div>

      <section className="max-w-7xl mx-auto px-5 lg:px-8 py-8 grid grid-cols-1 lg:grid-cols-12 gap-8">
        {/* Left: thumbnail strip + big image */}
        <div className="lg:col-span-7 grid grid-cols-[72px_1fr] gap-4">
          <div className="flex flex-col gap-3">
            {gallery.map((g, i) => (
              <button key={i} onClick={() => setActiveImg(i)}
                className={`relative aspect-square rounded-lg overflow-hidden border-2 transition ${activeImg === i ? "border-[color:var(--dms-peach)]" : "border-transparent hover:border-[color:var(--dms-navy)]/20"}`}
                style={{ background: color.hex }}>
                <img src={g} alt="" className="w-full h-full object-cover mix-blend-multiply" />
              </button>
            ))}
          </div>
          <div className="relative rounded-2xl overflow-hidden border border-[color:var(--dms-navy)]/10"
               style={{ background: color.hex }}>
            <img src={gallery[activeImg]} alt={product.title} className="w-full h-full object-cover mix-blend-multiply aspect-square" />
            <button onClick={() => wishToggle(product)} aria-label="Save"
              className="absolute top-4 right-4 w-10 h-10 rounded-full bg-white/90 backdrop-blur border border-[color:var(--dms-navy)]/10 flex items-center justify-center hover:bg-white transition">
              <Heart className={`w-4 h-4 ${wishHas(product.slug) ? "fill-red-500 text-red-500" : "text-[color:var(--dms-navy)]/60"}`} />
            </button>
            <button className="absolute top-4 left-4 bg-white/90 backdrop-blur border border-[color:var(--dms-navy)]/10 rounded-full px-3 py-2 text-[11px] tracking-widest uppercase font-semibold text-[color:var(--dms-navy)] inline-flex items-center gap-1.5">
              <Share2 className="w-3.5 h-3.5" /> Share
            </button>
            {activeImg > 0 && (
              <button onClick={() => setActiveImg((i) => Math.max(0, i - 1))} className="absolute left-3 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-white/90 border border-[color:var(--dms-navy)]/10 flex items-center justify-center">
                <ChevronLeft className="w-4 h-4 text-[color:var(--dms-navy)]" />
              </button>
            )}
            {activeImg < gallery.length - 1 && (
              <button onClick={() => setActiveImg((i) => Math.min(gallery.length - 1, i + 1))} className="absolute right-3 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-white/90 border border-[color:var(--dms-navy)]/10 flex items-center justify-center">
                <ChevronRight className="w-4 h-4 text-[color:var(--dms-navy)]" />
              </button>
            )}
          </div>
        </div>

        {/* Right: info / actions */}
        <div className="lg:col-span-5">
          {/* USP strip */}
          <div className="grid grid-cols-3 gap-3">
            <USP icon={PackageCheck} title="No minimum" sub="Order one or a thousand" />
            <USP icon={Truck} title="Free shipping" sub="On every U.S. order" />
            <USP icon={MapPin} title="Printed in USA" sub="Made with care" />
          </div>

          <p className="kicker mt-6">{product.category}</p>
          <h1 className="font-serif text-3xl md:text-4xl text-[color:var(--dms-navy)] mt-2 leading-tight">{product.title}</h1>

          {/* Rating */}
          <div className="mt-3 flex items-center gap-3">
            <div className="flex gap-0.5">
              {[1,2,3,4,5].map((n) => <Star key={n} className="w-4 h-4 fill-[color:var(--dms-peach)] text-[color:var(--dms-peach)]" />)}
            </div>
            <a href="#reviews" className="text-sm text-[color:var(--dms-navy)]/70 hover:text-[color:var(--dms-navy)]">Read reviews</a>
          </div>

          {/* Colors */}
          <div className="mt-6">
            <p className="text-[11px] tracking-widest uppercase text-[color:var(--dms-navy)]/60 font-semibold">Colors</p>
            <p className="text-sm text-[color:var(--dms-navy)] mt-1 font-medium">{color.name} <button onClick={() => setShowAllColors(!showAllColors)} className="text-[color:var(--dms-peach)] text-xs ml-2 underline underline-offset-4">{showAllColors ? "Hide" : "Check them all"}</button></p>
            <div className="mt-3 flex flex-wrap gap-2">
              {visibleColors.map((s) => (
                <button key={s.hex} onClick={() => setColor(s)} title={s.name}
                  className={`w-8 h-8 rounded-full border-2 transition ${color.hex === s.hex ? "border-[color:var(--dms-peach)] scale-110" : "border-[color:var(--dms-navy)]/15 hover:border-[color:var(--dms-navy)]/40"}`}
                  style={{ background: s.hex }} />
              ))}
            </div>
          </div>

          {/* Sizes */}
          <div className="mt-6">
            <p className="text-[11px] tracking-widest uppercase text-[color:var(--dms-navy)]/60 font-semibold">Sizes offered</p>
            <p className="text-sm text-[color:var(--dms-navy)]/80 mt-1">S — 4XL available at design stage</p>
            <p className="text-[11px] text-[color:var(--dms-navy)]/55 mt-1">Minimum quantity: <span className="font-semibold text-[color:var(--dms-navy)]">1</span></p>
          </div>

          {/* CTAs */}
          <div className="mt-7 flex flex-col sm:flex-row gap-3">
            <button onClick={startDesigning} data-testid="pd-start-designing" className="btn-peach flex-1 justify-center">Start Designing <ChevronRight className="w-4 h-4" /></button>
            <button onClick={addSample} className="btn-ghost-navy flex-1 justify-center">Order a Sample</button>
          </div>

          <button className="mt-4 text-sm text-[color:var(--dms-navy)]/60 hover:text-[color:var(--dms-navy)] inline-flex items-center gap-1">
            <Info className="w-4 h-4" /> Request a quote
          </button>
        </div>
      </section>

      {/* Description & Features */}
      <section className="max-w-7xl mx-auto px-5 lg:px-8 py-10">
        <div className="bg-white rounded-2xl border border-[color:var(--dms-navy)]/10 p-7 md:p-10">
          <h2 className="font-serif text-2xl md:text-3xl text-[color:var(--dms-navy)]">Description and Features</h2>
          <div className="w-14 h-[2px] bg-[color:var(--dms-peach)] mt-4" />
          <p className="mt-5 text-[color:var(--dms-navy)]/75 leading-relaxed">{product.longDesc}</p>
          <div className="mt-7 grid grid-cols-1 md:grid-cols-2 gap-6">
            <FeatureCol title="Fit" bullets={["Standard fit on body, chest and arms", "True to size — order your usual", "Unisex adult sizing"]} />
            <FeatureCol title="Fabric" bullets={["Soft, breathable premium fabric", "Reinforced seams and durable stitching", "Pre-shrunk, wash-safe"]} />
            <FeatureCol title="Your design" bullets={["Upload your own design (PDF, PNG or JPG)", "Or let us create one for you for $20", "Proof approval before every order"]} />
            <FeatureCol title="Sustainability" bullets={["Made in the United States", "Partnered with eco-conscious fabric mills", "Water-based inks, no harsh chemicals"]} />
          </div>
        </div>
      </section>

      {/* Delivery options */}
      <section className="max-w-7xl mx-auto px-5 lg:px-8 pb-10">
        <div className="bg-white rounded-2xl border border-[color:var(--dms-navy)]/10 p-7 md:p-10">
          <h2 className="font-serif text-2xl md:text-3xl text-[color:var(--dms-navy)]">Delivery Options</h2>
          <div className="w-14 h-[2px] bg-[color:var(--dms-peach)] mt-4" />
          <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4">
            <DeliveryCard title="Standard Delivery" eta="Arrives in 7–10 business days" price="Free" subtitle="On every U.S. order" />
            <DeliveryCard title="Rush Delivery" eta="Arrives in 4–6 business days" price="Add $9.99" subtitle="Faster production + ship" />
            <DeliveryCard title="Super Rush" eta="Arrives in 2–3 business days" price="Add $19.99" subtitle="Weekend events, no problem" />
          </div>
          <p className="mt-6 text-sm text-[color:var(--dms-navy)]/65">
            <span className="font-semibold text-[color:var(--dms-navy)]">Ships to all 50 states.</span> Every order is printed in the USA, inspected by hand and sent out with a tracking link emailed to you.
          </p>
        </div>
      </section>

      {/* Reviews */}
      <section id="reviews" className="max-w-7xl mx-auto px-5 lg:px-8 pb-16">
        <Reviews productSlug={product.slug} />
      </section>

      <Footer />
    </div>
  );
}

function USP({ icon: Icon, title, sub }) {
  return (
    <div className="rounded-xl bg-white border border-[color:var(--dms-navy)]/10 p-3 text-center">
      <Icon className="w-4 h-4 text-[color:var(--dms-peach)] mx-auto" />
      <p className="font-serif text-[color:var(--dms-navy)] text-sm mt-1.5 leading-tight">{title}</p>
      <p className="text-[10px] text-[color:var(--dms-navy)]/55 mt-0.5 leading-tight">{sub}</p>
    </div>
  );
}

function FeatureCol({ title, bullets }) {
  return (
    <div>
      <p className="text-[11px] tracking-widest uppercase text-[color:var(--dms-peach)] font-semibold">{title}</p>
      <ul className="mt-3 space-y-2">
        {bullets.map((b) => <li key={b} className="text-sm text-[color:var(--dms-navy)]/80 flex gap-2"><span className="text-[color:var(--dms-peach)] mt-0.5">•</span> {b}</li>)}
      </ul>
    </div>
  );
}

function DeliveryCard({ title, eta, price, subtitle }) {
  return (
    <div className="rounded-xl border border-[color:var(--dms-navy)]/10 p-5">
      <p className="kicker text-[10px]">{title}</p>
      <p className="mt-2 font-serif text-xl text-[color:var(--dms-navy)]">{price}</p>
      <p className="text-xs text-[color:var(--dms-navy)]/65 mt-1">{eta}</p>
      <p className="text-xs text-[color:var(--dms-navy)]/55 mt-2">{subtitle}</p>
    </div>
  );
}
